import { Button, DropdownMenu, Heading, Tag } from '@humanforce/mango-ui-react';
import type { PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunTimelineAction, PayRunTimelineActionVerb } from './payRunTimelineModel';

interface PayRunDetailTitleProps {
  payRun: PayRunDetailDto;
  action?: PayRunTimelineAction | null;
  actionBusy?: boolean;
  onAction?: (verb: PayRunTimelineActionVerb) => void;
  secondaryActions?: PayRunTitleSecondaryAction[];
  onSecondaryAction?: (verb: PayRunTitleSecondaryAction['verb']) => void;
}

export interface PayRunTitleSecondaryAction {
  verb: 'edit' | 'revert_to_trial' | 'unlock_to_compliance' | 'cancel';
  label: string;
  destructive?: boolean;
}

const date = (value: string): string =>
  new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

export const PayRunDetailTitle = ({
  payRun,
  action,
  actionBusy = false,
  onAction,
  secondaryActions = [],
  onSecondaryAction,
}: PayRunDetailTitleProps) => {
  const operatingStateLabel = payRun.workflowStatus === 'cancelled'
    ? 'Cancelled'
    : payRun.operatingState === 'trial_pay'
      ? 'Trial pay'
      : payRun.operatingState === 'payroll_active' ? 'Active pay' : 'Status unavailable';

  return (
  <div className="mgo-split mgo-gap-l mgo-align-start" data-testid="pay-run-detail-title">
    <div className="mgo-stack mgo-gap-xs">
      <div className="mgo-cluster mgo-gap-s mgo-align-center">
        <Heading level={1}>{payRun.displayTitle}</Heading>
        <Tag
          label={operatingStateLabel}
          tone={payRun.operatingState === 'payroll_active' ? 'positive' : 'neutral'}
        />
      </div>
      <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>
        Pay period {date(payRun.periodStartDate)}–{date(payRun.periodEndDate)}
        {' '}· Payment date <strong>{date(payRun.paymentDate)}</strong>
      </p>
    </div>
    <div className="mgo-cluster mgo-gap-s mgo-align-center">
      {action && (
        <Button
          label={action.label}
          loading={actionBusy}
          disabled={actionBusy}
          testId="pay-run-detail-title-action"
          onClick={() => onAction?.(action.verb)}
        />
      )}
      {secondaryActions.length > 0 && (
        <DropdownMenu
          testId="pay-run-detail-secondary-actions"
          trigger={<Button label="More actions" variant="secondary" disabled={actionBusy} />}
          items={secondaryActions.map((item) => ({
            label: item.label,
            variant: item.destructive ? 'destructive' : 'default',
            disabled: actionBusy,
            onClick: () => onSecondaryAction?.(item.verb),
          }))}
        />
      )}
    </div>
  </div>
  );
};
